// @ts-nocheck

import React, { useRef, useMemo } from 'react';
import ReactResizeDetector from 'react-resize-detector';
import { AutoSizer, List, WindowScroller } from 'react-virtualized';
import styled, { css } from 'styled-components';

import { Target } from '../../../../client';
import { getAppDOMElement } from '../../../../DOMElements';
import { ReactHeight } from '../../../../lib/react-height.custom';
import GridsSystemGridContainer from '../../containers/grid/GridsSystemGridContainer';
import GridsResetSelectionContainer from '../../containers/GridsResetSelectionContainer';
import GridsSystemActionsContainer from '../../containers/GridsSystemActionsContainer';
import GridsFlashButtonContainer from '../../containers/pool/GridsFlashButtonContainer';

import SystemGridAmount from './SystemGridAmount';

const getElementHeight = (el) => el.offsetHeight;

const Wrapper = styled.div`
  .ReactVirtualized__Grid,
  .ReactVirtualized__Grid__innerScrollContainer {
    overflow: visible !important;
  }
`;

const WrapButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1em 0;
  padding: 1em;
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.grids.colors.darkgrey3};
  margin: 2em 0;
`;

const StyledGridsSystemActionsContainer = styled(GridsSystemActionsContainer)`
  ${({ theme }) =>
    !Target.__MOBILE__
      ? css`
          padding: 1em 0;
          margin: 1em 0;
          ${theme.grids.desktopBloc}
        `
      : undefined}
`;

const StyledGridsSystemGridContainer = styled(GridsSystemGridContainer)``;

const StyledResetSelectionContainer = styled(GridsResetSelectionContainer)``;

const GridBloc = styled.div`
  ${({ theme }) =>
    !Target.__MOBILE__
      ? css`
          padding: 2em 0;
          margin: 0 0 1em 0;
          ${theme.grids.desktopBloc}
        `
      : undefined}
`;

// use to force virtualized to see bottom margin
const DesktopMargin = styled.div`
  height: 1px;
  margin: 0;
  padding: 0;
`;

const overscanIndicesGetter = (options) => {
  const { cellCount, overscanCellsCount, scrollDirection, startIndex, stopIndex } = options;

  let overscanStartIndex = 0;
  let overscanStopIndex = 0;
  switch (scrollDirection) {
    case 1:
    case -1:
      overscanStartIndex = startIndex - overscanCellsCount;
      overscanStopIndex = stopIndex + overscanCellsCount;
      if (overscanStartIndex < 0) {
        overscanStopIndex += Math.abs(overscanStartIndex);
      }
      break;
    default:
      break;
  }
  return {
    overscanStartIndex: Math.max(0, overscanStartIndex),
    overscanStopIndex: Math.min(cellCount - 1, overscanStopIndex),
  };
};

type GridsVirtualizedSystemListProps = {
  className?: string;
  grids: Array<{
    selectionIndex: number;
    amount: string;
    selection: string;
  }>;
  poolId: number;
  isReadOnly: boolean;
};

const GridSystemBloc = ({ poolId, selection, selectionIndex, amount, isReadOnly }) => {
  return (
    <div>
      {isReadOnly && Target.__MOBILE__ && <Line />}
      <GridBloc>
        <SystemGridAmount amount={amount} />
        <StyledGridsSystemGridContainer
          poolId={poolId}
          selection={selection}
          selectionIndex={selectionIndex}
          isReadOnly={isReadOnly}
        />
        {!isReadOnly && (
          <>
            <WrapButtons>
              <StyledResetSelectionContainer selectionIndex={selectionIndex} />
              <GridsFlashButtonContainer selectionIndex={selectionIndex} />
            </WrapButtons>
          </>
        )}
      </GridBloc>
      {!isReadOnly && <StyledGridsSystemActionsContainer selectionIndex={selectionIndex} />}
      {!Target.__MOBILE__ && <DesktopMargin />}
    </div>
  );
};

const GridsVirtualizedSystemList = ({
  className,
  grids,
  poolId,
  isReadOnly = false,
}: GridsVirtualizedSystemListProps) => {
  const cache = useRef({});
  const renderListRef = useRef();
  const scrollElement = useMemo(() => (Target.__MOBILE__ ? getAppDOMElement() : window), []);

  const rowHeight = ({ index }) => {
    return cache.current[index] || (Target.__MOBILE__ ? 700 : 530);
  };

  const renderRow = ({ index, key, style, width }) => {
    const item = grids[index];
    return (
      <div style={style} key={key}>
        <ReactHeight
          getElementHeight={getElementHeight}
          onHeightReady={(height) => {
            cache.current[index] = height;
            if (renderListRef.current) {
              renderListRef.current.recomputeRowHeights(index);
            }
          }}
          style={{ width }}
        >
          <GridSystemBloc {...item} isReadOnly={isReadOnly} index={index} poolId={poolId} />
          <ReactResizeDetector
            handleHeight
            onResize={(w, height) => {
              cache.current[index] = height;
              if (renderListRef.current) {
                renderListRef.current.recomputeRowHeights(index);
              }
            }}
          />
        </ReactHeight>
      </div>
    );
  };

  const rowCount = grids ? grids?.length : null;
  if (rowCount && rowCount === 0) {
    return null;
  }

  return (
    <Wrapper className={className}>
      <WindowScroller scrollElement={scrollElement}>
        {({ height, isScrolling, scrollTop, onChildScroll }) => (
          <AutoSizer disableHeight>
            {({ width }) => (
              <List
                ref={renderListRef}
                isScrolling={isScrolling}
                autoHeight
                height={height || 0}
                width={width || 0}
                rowCount={rowCount}
                overscanRowCount={Target.__MOBILE__ ? 5 : 10}
                overscanIndicesGetter={overscanIndicesGetter}
                rowHeight={rowHeight}
                rowRenderer={renderRow}
                scrollToAlignment="start"
                scrollTop={scrollTop}
                items={grids}
                onScroll={onChildScroll}
              />
            )}
          </AutoSizer>
        )}
      </WindowScroller>
    </Wrapper>
  );
};

export default GridsVirtualizedSystemList;
